import { toDateInputValue } from '../../helpers/format.js';
import type { RecordRef } from '../../models/company-data.js';
import type { DataField } from './definition.js';
import { isRecordRef } from './records-table.js';

export interface ReferenceOption {
  ref: RecordRef;
  label: string;
}

// What an input shows for a stored value; checkboxes read the value directly
export function toInputValue(field: DataField, value: unknown): string {
  if (value === undefined || value === null) return '';
  switch (field.kind) {
    case 'date':
      return toDateInputValue(value as string);
    case 'reference':
      return isRecordRef(value) ? value.recordId : '';
    default:
      return String(value);
  }
}

// The value to store for what an input holds, or undefined to leave the field empty
export function fromInputValue(field: DataField, raw: string | boolean, options: ReferenceOption[] = []): unknown {
  if (field.kind === 'yesNo') return Boolean(raw);
  const text = String(raw);
  if (text.trim() === '') return undefined;
  switch (field.kind) {
    case 'number': {
      const number = Number(text);
      return Number.isFinite(number) ? number : text;
    }
    case 'reference':
      return options.find((option) => option.ref.recordId === text)?.ref;
    default:
      return text;
  }
}

// The lambda names each field it refuses ("'hours' must be a number."), so each sentence goes under its field
export function fieldErrors(
  message: string,
  fields: Pick<DataField, 'key' | 'label'>[],
): { byField: Record<string, string>; general: string[] } {
  const byField: Record<string, string> = {};
  const general: string[] = [];
  const sentences = message.replace(/^Validation:\s*/, '').match(/[^.]+\.?/g) ?? [];
  for (const raw of sentences.map((sentence) => sentence.trim()).filter(Boolean)) {
    const key = raw.match(/^'([^']+)'/)?.[1];
    const field = fields.find((candidate) => candidate.key === key);
    if (field) byField[field.key] = raw.replace(`'${field.key}'`, field.label);
    else general.push(raw);
  }
  return { byField, general };
}
